import React from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { ContentType, Heading } from '@atlassian/learning-components';
import { useLocation, useNavigate } from 'react-router-dom';
import Breadcrumbs, { BreadcrumbsItem } from '@atlaskit/breadcrumbs';

import { TagList } from 'src/components/TagList';
import { getHubUrl } from 'src/utils/getUrl';
import { ContentHubType, ResourceType } from 'src/constants';
import { generateBreadcrumbLinks } from 'src/components/utils/generateBreadcrumbLinks';
import { getContentHeaderContent } from 'src/components/utils/getContentHeaderContent';
import { ContentfulLearningPath } from 'src/graphql/queries/GetLearningPath';
import { ContentfulSmCourse } from 'src/graphql/queries/GetSmCourse';
import { GetLearningPathId } from 'src/graphql/queries/GetLearningPathId';

interface ContentHeaderProps {
  content: ContentfulLearningPath | GetLearningPathId | ContentfulSmCourse;
  contentType: ContentType.LEARNING_PATH | ContentType.COURSE;
  learningPath?: GetLearningPathId;
}

export const ContentHeader: React.FC<ContentHeaderProps> = ({ content, contentType, learningPath }) => {
  const isLearningPath = contentType === ContentType.LEARNING_PATH;
  const currentLearningPath = isLearningPath ? (content as ContentfulLearningPath) : learningPath;
  const course = !isLearningPath ? (content as ContentfulSmCourse) : undefined;

  const { title, tagline, product, productRole } = getContentHeaderContent(content, isLearningPath);
  const location = useLocation();
  const navigate = useNavigate();

  const hasRoles = productRole && productRole.length > 0;
  const isAllLearningPathsHubIncluded = location.pathname?.includes(getHubUrl(ContentHubType.LEARNING_PATHS));
  const isLearningPathIncluded = location.pathname?.includes('path/');

  const isBreadcrumbVisible = isLearningPath
    ? isAllLearningPathsHubIncluded
    : isAllLearningPathsHubIncluded || isLearningPathIncluded;

  const breadcrumbLinks = generateBreadcrumbLinks(currentLearningPath, course);
  const contentTypePrefix = isLearningPath ? ResourceType.LearningPath : ResourceType.SmCourse;

  return (
    <div css={headerContainerStyles} data-testid={`${contentTypePrefix}-header`}>
      <div css={headingAndSubheadingStyles} data-testid="heading-and-subheader">
        {isBreadcrumbVisible && breadcrumbLinks.length > 0 && (
          <div css={breadcrumbContainerStyles} data-testid="breadcrumbs-container">
            <Breadcrumbs itemsBeforeCollapse={0} itemsAfterCollapse={1}>
              {breadcrumbLinks.map((link, index) => (
                <BreadcrumbsItem
                  key={link.label}
                  text={link.label}
                  href={link.link}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(link.link);
                  }}
                  data-testid={`breadcrumb-link-${index}`}
                />
              ))}
            </Breadcrumbs>
          </div>
        )}
        <Heading level={1} color={token('color.text')} data-testid={`${contentTypePrefix}-title`}>
          {title}
        </Heading>
        {tagline && (
          <span css={subHeadingStyles} data-testid={`${contentTypePrefix}-tagline`}>
            {tagline}
          </span>
        )}
      </div>
      <div css={metadataContainerStyles} data-testid="metadata-container">
        {product && product.length > 0 && (
          <div css={metadataGroupStyles} data-testid="products-group">
            <span css={metadataLabelStyles} data-testid="products-label">
              PRODUCTS
            </span>
            <div css={metadataItemsStyles} data-testid="products-items">
              <TagList isLarge={true} taxonomyList={product || []} />
            </div>
          </div>
        )}
        {hasRoles && (
          <div css={metadataGroupStyles} data-testid="role-group">
            <span css={metadataLabelStyles} data-testid="role-label">
              ROLE
            </span>
            <div css={metadataItemsStyles} data-testid="role-items">
              <span css={metadataValueStyles} data-testid="role-value">
                {(productRole || []).join(', ')}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const headerContainerStyles = css({
  display: 'flex',
  maxWidth: '813px',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: token('space.300'),
  paddingBottom: '18px',

  '@media (max-width: 1279px)': {
    maxWidth: '513px',
  },

  '@media (max-width: 965px)': {
    paddingBottom: token('space.300'),
    width: '100%',
    maxWidth: 'unset',
  },
});

const headingAndSubheadingStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: token('space.050'),
  flex: '1 0 0',
});

const breadcrumbContainerStyles = css({
  display: 'flex',
  minHeight: '24px',
  alignItems: 'center',
  gap: token('space.100'),

  '& a, & span, & nav, & li': {
    color: token('color.text.subtle'),
    textAlign: 'center',
    fontFamily: 'Inter, sans-serif',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '24px',
  },
});

const metadataContainerStyles = css({
  display: 'flex',
  alignItems: 'baseline',
  gap: token('space.300'),

  '@media (max-width: 965px)': {
    flexDirection: 'column',
    gap: token('space.100'),
    borderTop: `1px solid ${token('color.border')}`,
    borderBottom: `1px solid ${token('color.border')}`,
    padding: `${token('space.200')}  0`,
    width: '100%',
  },
});

const metadataGroupStyles = css({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-start',
  gap: token('space.050'),

  '@media (max-width: 965px)': {
    display: 'flex',
    gap: token('space.200'),
    alignSelf: 'stretch',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    width: '100%',
    alignItems: 'baseline',
  },
});

const metadataItemsStyles = css({
  display: 'flex',
  flexWrap: 'wrap',
  width: '100%',
  maxWidth: 'calc(813px / 2)',

  '@media (max-width: 1279px)': {
    maxWidth: 'calc(513px / 2)',
  },

  '@media (max-width: 965px)': {
    maxWidth: 'none',
    flex: 1,
    gap: token('space.050'),
  },
});

const metadataLabelStyles = css({
  color: '#172B4D',
  fontFamily: 'Charlie Display, sans-serif',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: '600',
  lineHeight: '24px',

  '@media (max-width: 965px)': {
    fontSize: '14px',
    lineHeight: '20px',
  },
});

const subHeadingStyles = css({
  color: '#172B4D',
  fontFamily: 'Inter, sans-serif',
  fontSize: '20px',
  fontStyle: 'normal',
  fontWeight: '400',
  lineHeight: '32px',
});

const metadataValueStyles = css({
  color: '#172B4D',
  fontFamily: 'Inter, sans-serif',
  fontSize: '20px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '32px',

  '@media (max-width: 965px)': {
    fontSize: '14px',
    lineHeight: '20px',
  },
});
